import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type AccountProfileDto } from '../lib/api';
import { useAuth } from '../lib/auth';

// PUT body for the account update. Extends the profile DTO with the optional
// password fields the legacy Account Settings form submitted alongside the
// email and newsletter opt-in.
type UpdateAccountBody = AccountProfileDto & {
  password?: string;
  passwordConfirmation?: string;
};

/**
 * Account Settings — mirrors the legacy `resources/views/accountsettings.blade.php`.
 * Only exposes what the legacy tool had: email, an optional password change
 * (change + confirm, no current-password field) and the newsletter opt-in. The
 * remaining profile fields (name, phone, company, address, customer type) are
 * loaded and re-submitted untouched so they are preserved.
 */
export function AccountPage() {
  const qc = useQueryClient();
  const { user, setUser } = useAuth();

  const q = useQuery({
    queryKey: ['account-profile'],
    queryFn: () => api.get<AccountProfileDto>('/api/v1/auth/account'),
  });

  const [profile, setProfile] = useState<AccountProfileDto | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [optIn, setOptIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (q.data) {
      setProfile(q.data);
      setEmail(q.data.email ?? '');
      setOptIn(q.data.optInNewsletter);
    }
  }, [q.data]);

  const save = useMutation({
    mutationFn: (body: UpdateAccountBody) => api.put<AccountProfileDto>('/api/v1/auth/account', body),
    onSuccess: (data) => {
      qc.setQueryData(['account-profile'], data);
      setProfile(data);
      setPassword('');
      setConfirm('');
      setOptIn(data.optInNewsletter);
      if (user) {
        setUser({ ...user, name: data.name, email: data.email, companyName: data.company });
      }
      setError(null);
      setDone(true);
    },
    onError: (e) => {
      setDone(false);
      if (e instanceof ApiError) {
        if (e.status === 409) { setError('Email already in use.'); return; }
        if (e.status === 400) {
          const code = (e.body as { error?: string } | undefined)?.error;
          if (code === 'passwords_do_not_match') { setError('Passwords did not match'); return; }
          if (code === 'password_too_short') { setError('Password not secure enough'); return; }
        }
      }
      setError('Could not update account.');
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setDone(false);
    setError(null);
    if (!profile) return;

    if (password !== '') {
      if (password !== confirm) { setError('Passwords did not match'); return; }
      if (password.length < 8) { setError('Password not secure enough'); return; }
    }

    save.mutate({
      ...profile,
      email,
      optInNewsletter: optIn,
      password: password || undefined,
      passwordConfirmation: confirm || undefined,
    });
  };

  return (
    <div className="container-fluid mt-2">
      <form onSubmit={onSubmit} noValidate>
        <div className="row">
          <div className="col-12">
            <h1 className="text-center">Account Settings</h1>
            <div className="row justify-content-center">
              {error && (
                <div className="col-lg-10 col-md-12">
                  <div className="alert alert-danger">{error}</div>
                </div>
              )}
              {done && (
                <div className="col-lg-10 col-md-12">
                  <div className="alert alert-success">Your account has been updated.</div>
                </div>
              )}

              {!profile ? (
                <div className="col-lg-10 col-md-12">
                  <p className="text-muted text-center">Loading…</p>
                </div>
              ) : (
                <>
                  <div className="col-lg-10 col-md-12">
                    <div className="form-floating mb-2">
                      <input
                        type="email"
                        className="form-control border border-3"
                        id="email"
                        placeholder="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                      <label htmlFor="email">Email</label>
                    </div>
                  </div>

                  <div className="col-lg-5 col-md-12">
                    <div className="form-floating mb-2">
                      <input
                        type="password"
                        id="password"
                        className="form-control"
                        placeholder="Enter your password"
                        autoComplete="new-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                      <label htmlFor="password">Change Password*</label>
                    </div>
                  </div>
                  <div className="col-lg-5 col-md-12">
                    <div className="form-floating mb-2">
                      <input
                        type="password"
                        id="password_confirmation"
                        className="form-control"
                        placeholder="Please enter your password again"
                        autoComplete="new-password"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                      />
                      <label htmlFor="password_confirmation">Confirm Password*</label>
                    </div>
                  </div>

                  <div className="col-lg-10 py-1 col-md-12">
                    <div className="mb-2">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="checkbox-signup"
                          checked={optIn}
                          onChange={(e) => setOptIn(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="checkbox-signup">
                          Sign me up to receive the latest news and product information.
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="mb-3 text-center">
                    <button className="btn btn-primary" type="submit" disabled={save.isPending}>
                      {save.isPending ? 'Updating…' : 'Update Account'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
